// renderer/DynamicField.tsx
import React from 'react';
import { Group, TextInput, Textarea, NumberInput, Select, MultiSelect, Switch, Stack, Image, Text, ActionIcon, Center } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { BuilderField, FieldOption } from '../types/BuilderField';
import { DateTimePicker, DateInput } from '@mantine/dates';
import { FileUploader } from '@/components/ccms-components/file-management';
import { fromSDKDate } from '@/utils/dateTime';

/**
 * Mantine's date pickers crash on anything that isn't a real Date, but saved
 * answers come back as an SDK timestamp (yyyyMMddHHmmss), an ISO string, or a
 * JSON-quoted ISO string. Unparseable values render as empty instead.
 */
function toDateValue(value: unknown): Date | null {
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value).replace(/^"|"$/g, '');
  if (/^\d+$/.test(raw)) return fromSDKDate(raw);
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

export interface DynamicFieldProps {
  field: BuilderField;
  value?: any;
  onChange?: (value: any) => void;
  disabled?: boolean;
  uploadDomain?: string;
  uploadRecordId?: string | number;
  error?: string;
  /** Choices for 'member' fields — supplied by the page, since they depend on the record being filled in. */
  memberOptions?: FieldOption[];
}

/**
 * Renders a single input for a BuilderField. Used identically by the builder's
 * live preview (FormPreview) and by DynamicForm at runtime — one rendering
 * engine, no duplicated logic anywhere.
 */
export function DynamicField({ field, value, onChange, disabled, uploadDomain, uploadRecordId, error, memberOptions }: DynamicFieldProps) {
  const commonProps = {
    label: field.label,
    placeholder: field.placeholder,
    required: field.required,
    disabled,
    error,
  };

  switch (field.fieldType) {
    case 'textarea':
      return (
        <Textarea
          {...commonProps}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.currentTarget.value)}
          autosize
          minRows={2}
        />
      );
    case 'number':
    case 'decimal':
      return (
        <NumberInput
          {...commonProps}
          value={value ?? ''}
          onChange={(v) => onChange?.(v)}
          decimalScale={field.fieldType === 'decimal' ? 2 : 0}
        />
      );
    case 'boolean':
      return (
        <Stack gap={2}>
          <Group gap="sm" align="center" wrap="nowrap">
            <Switch
              label={field.label}
              required={field.required}
              disabled={disabled}
              checked={!!value}
              onChange={(e) => onChange?.(e.currentTarget.checked)}
            />
          </Group>
          {error && <Text size="xs" c="red">{error}</Text>}
        </Stack>
      );
    case 'date':
      return (
        <DateInput
          {...commonProps}
          value={toDateValue(value)}
          onChange={(val) => onChange?.(val)}
        />
      );
    case 'datetime':
      return (
        <DateTimePicker
          {...commonProps}
          value={toDateValue(value)}
          onChange={(val) => onChange?.(val)}
        />
      );
    case 'email':
      return (
        <TextInput
          {...commonProps}
          type="email"
          value={value ?? ''}
          onChange={(e) => onChange?.(e.currentTarget.value)}
        />
      );
    case 'phone':
      return (
        <TextInput
          {...commonProps}
          type="tel"
          value={value ?? ''}
          onChange={(e) => onChange?.(e.currentTarget.value)}
        />
      );
    case 'url':
      return (
        <Stack gap="xs">
          {field.label && (
            <Text size="sm" fw={500}>
              {field.label}
              {field.required && <Text component="span" c="red"> *</Text>}
            </Text>
          )}

          {value ? (
            <Center>
              <Group gap="xs" align="center" justify="center">
                <Image
                  src={value}
                  alt={field.label || 'Uploaded image'}
                  radius="sm"
                  h={220}
                  w={220}
                  fit="cover"
                  fallbackSrc="/Course-placeholder.png"
                />
                {!disabled && (
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => onChange?.('')}
                    aria-label="Remove image"
                  >
                    <IconX size={16} />
                  </ActionIcon>
                )}
              </Group>
            </Center>
          ) : (
            !disabled && (
              <FileUploader
                domain={uploadDomain ?? 'FormField'}
                recordId={String(uploadRecordId ?? field.id)}
                multiple={false}
                accept={['image/*']}
                variant="dropzone"
                autoUpload
                onUploadComplete={(files) => {
                  const uploaded = Array.isArray(files) ? files[0] : files;
                  const url = (uploaded as any)?.data?.url ?? (uploaded as any)?.url;
                  if (url) onChange?.(url);
                }}
              />
            )
          )}

          {disabled && !value && (
            <Text size="sm" c="dimmed">No image uploaded</Text>
          )}
          {error && <Text size="xs" c="red">{error}</Text>}
        </Stack>
      );
    case 'select':
      return (
        <Select
          {...commonProps}
          data={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
          value={value ?? null}
          onChange={(v) => onChange?.(v)}
          clearable
        />
      );
    case 'multiselect':
      return (
        <MultiSelect
          {...commonProps}
          data={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
          value={value ?? []}
          onChange={(v) => onChange?.(v)}
          clearable
        />
      );
    case 'member':
      return (
        <Select
          {...commonProps}
          data={memberOptions ?? []}
          // Saved answers come back as a number (valueNumber); Select matches on strings.
          value={value === null || value === undefined || value === '' ? null : String(value)}
          onChange={(v) => onChange?.(v)}
          searchable
          clearable
          nothingFoundMessage="No members on this registration"
        />
      );
    case 'text':
    default:
      return (
        <TextInput
          {...commonProps}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.currentTarget.value)}
        />
      );
  }
}